import React, { useState, useEffect } from 'react';
import './adminHomeProducts.css';
import productsService from '../../../services/products.service';
import homeProductsService from '../../../services/homeProducts.service';
import ImageUpload from '../../../components/common/ImageUpload/ImageUpload';
import { UPLOAD_FOLDERS } from '../../../services/cloudinary/cloudinary.service';
import { useToast } from '../../../context/ToastContext';

const AdminHomeProducts = () => {
  const toast = useToast();
  const [allProducts, setAllProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedFeatured, setSelectedFeatured] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    productId: '',
    title: '',
    description: '',
    price: '',
    features: [],
    images: [], // Array of image objects with url and publicId
    imagePosition: 'left',
    badge: '',
    isActive: true,
    order: 0
  });

  const [featureInput, setFeatureInput] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsResult, featuredResult] = await Promise.all([
        productsService.getAllProducts(),
        homeProductsService.getAllFeaturedProducts()
      ]);

      if (productsResult.success) {
        setAllProducts(productsResult.products);
      }
      if (featuredResult.success) {
        setFeaturedProducts(featuredResult.featured);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get product image URL
  const getProductImageUrl = (product) => {
    if (Array.isArray(product.images) && product.images.length > 0) {
      const firstImage = product.images[0];
      return typeof firstImage === 'object' ? firstImage.url : firstImage;
    }
    if (typeof product.image === 'object' && product.image?.url) {
      return product.image.url;
    }
    if (typeof product.image === 'string') {
      return product.image;
    }
    return '';
  };

  // Helper function to normalize product images to array format
  const normalizeProductImages = (product) => {
    let imagesArray = [];
    if (Array.isArray(product.images)) {
      imagesArray = product.images.map(img => 
        typeof img === 'object' ? img : { url: img }
      );
    } else if (product.image) {
      const imgObj = typeof product.image === 'object' ? product.image : { url: product.image };
      imagesArray = [imgObj];
    }
    return imagesArray;
  };

  const handleProductSelect = (e) => {
    const productId = e.target.value;
    setSelectedProductId(productId);
    
    const product = allProducts.find(p => p.id === productId);
    if (product) {
      const productImages = normalizeProductImages(product);
      
      setFormData(prev => ({
        ...prev,
        productId: productId,
        title: product.name,
        description: product.description,
        price: `À partir de ${product.price.toLocaleString()} DA`,
        features: product.features || [],
        images: productImages
      }));
    }
  };

  const handleAdd = () => {
    setModalMode('add');
    setSelectedProductId('');
    setFormData({
      productId: '',
      title: '',
      description: '',
      price: '',
      features: [],
      images: [],
      imagePosition: 'left',
      badge: '',
      isActive: true,
      order: featuredProducts.length
    });
    setShowModal(true);
  };

  const handleEdit = (featured) => {
    setModalMode('edit');
    setSelectedFeatured(featured);
    setSelectedProductId(featured.productId);
    
    // Normalize images to array format
    let imagesArray = [];
    if (Array.isArray(featured.images)) {
      imagesArray = featured.images.map(img => 
        typeof img === 'object' ? img : { url: img }
      );
    }
    
    setFormData({
      productId: featured.productId,
      title: featured.title,
      description: featured.description,
      price: featured.price,
      features: featured.features || [],
      images: imagesArray,
      imagePosition: featured.imagePosition || 'left',
      badge: featured.badge || '',
      isActive: featured.isActive !== false,
      order: featured.order || 0
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle multiple image uploads
  const handleImageUpload = (imageData) => {
    if (!imageData) return;
    
    if (Array.isArray(imageData)) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...imageData]
      }));
    } else if (imageData && typeof imageData === 'object') {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, imageData]
      }));
    }
  };

  // Remove image from featured product
  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Set primary image (move to first position)
  const handleSetPrimaryImage = (index) => {
    setFormData(prev => {
      const newImages = [...prev.images];
      const [primaryImage] = newImages.splice(index, 1);
      newImages.unshift(primaryImage);
      return { ...prev, images: newImages };
    });
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, featureInput.trim()]
      }));
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate that at least one image is uploaded
    if (!formData.images || formData.images.length === 0) {
      toast.showInfo('Veuillez télécharger au moins une image pour le produit vedette');
      return;
    }
    
    submitData();
  };

  const submitData = async () => {
    setLoading(true);

    try {
      const featuredData = {
        ...formData,
        images: formData.images // Store array of image objects
      };

      let result;
      if (modalMode === 'add') {
        result = await homeProductsService.addFeaturedProduct(featuredData);
      } else {
        result = await homeProductsService.updateFeaturedProduct(selectedFeatured.id, featuredData);
      }

      if (result.success) {
        await fetchData();
        setShowModal(false);
        toast.showSuccess(`Produit vedette ${modalMode === 'add' ? 'ajouté' : 'modifié'} avec succès!`);
      } else {
        toast.showError('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Error submitting featured product:', error);
      toast.showError('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit vedette?')) {
      return;
    }

    setLoading(true);
    try {
      const result = await homeProductsService.deleteFeaturedProduct(id);
      if (result.success) {
        await fetchData();
        toast.showSuccess('Produit vedette supprimé avec succès!');
      } else {
        toast.showError('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Error deleting featured product:', error);
      toast.showError('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (featured) => {
    setLoading(true);
    try {
      const result = await homeProductsService.updateFeaturedProduct(featured.id, {
        ...featured,
        isActive: !featured.isActive
      });
      if (result.success) {
        await fetchData();
      }
    } catch (error) {
      console.error('Error toggling active:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = allProducts.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Helper to get first image URL from featured product
  const getFeaturedImageUrl = (featured) => {
    if (Array.isArray(featured.images) && featured.images.length > 0) {
      const firstImage = featured.images[0];
      return typeof firstImage === 'object' ? firstImage.url : firstImage;
    }
    return '';
  };

  return (
    <div className="admin-homepro-admin-home-products">
      <div className="admin-homepro-products-header">
        <div className="admin-homepro-header-left">
          <h1 className="admin-homepro-page-title">Produits Vedettes - Page d'Accueil</h1>
          <p className="admin-homepro-page-subtitle">{featuredProducts.length} produits vedettes configurés</p>
        </div>
        <button className="admin-homepro-btn admin-homepro-btn-primary" onClick={handleAdd}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Ajouter un produit vedette
        </button>
      </div>

      {loading ? (
        <div className="admin-homepro-loading-state">
          <div className="admin-homepro-spinner"></div>
          <p>Chargement...</p>
        </div>
      ) : featuredProducts.length === 0 ? (
        <div className="admin-homepro-empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
          <h3>Aucun produit vedette</h3>
          <p>Commencez par ajouter votre premier produit vedette</p>
        </div>
      ) : (
        <div className="admin-homepro-featured-grid">
          {featuredProducts.map(featured => (
            <div key={featured.id} className={`admin-homepro-featured-card ${!featured.isActive ? 'admin-homepro-inactive' : ''}`}>
              <div className="admin-homepro-featured-card-header">
                <span className={`admin-homepro-status-indicator ${featured.isActive ? 'admin-homepro-active' : 'admin-homepro-inactive'}`}>
                  {featured.isActive ? 'Actif' : 'Inactif'}
                </span>
                {featured.badge && (
                  <span className="admin-homepro-featured-badge">{featured.badge}</span>
                )}
              </div>

              <div className="admin-homepro-featured-card-images">
                <img src={getFeaturedImageUrl(featured)} alt={featured.title} className="admin-homepro-main-image" />
                {featured.images && featured.images.length > 1 && (
                  <div className="admin-homepro-image-count">+{featured.images.length - 1}</div>
                )}
              </div>

              <div className="admin-homepro-featured-card-content">
                <h3 className="admin-homepro-featured-card-title">{featured.title}</h3>
                <p className="admin-homepro-featured-card-description">{featured.description}</p>
                <div className="admin-homepro-featured-card-price">{featured.price}</div>
                
                {featured.features && featured.features.length > 0 && (
                  <div className="admin-homepro-featured-card-features">
                    {featured.features.slice(0, 3).map((feature, idx) => (
                      <span key={idx} className="admin-homepro-feature-tag-mini">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        {feature}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="admin-homepro-featured-card-actions">
                <button
                  className={`admin-homepro-btn-toggle ${featured.isActive ? 'admin-homepro-active' : ''}`}
                  onClick={() => toggleActive(featured)}
                  title={featured.isActive ? 'Désactiver' : 'Activer'}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {featured.isActive ? (
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    ) : (
                      <>
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </>
                    )}
                  </svg>
                </button>
                <button
                  className="admin-homepro-btn-icon admin-homepro-btn-edit"
                  onClick={() => handleEdit(featured)}
                  title="Modifier"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
                <button
                  className="admin-homepro-btn-icon admin-homepro-btn-delete"
                  onClick={() => handleDelete(featured.id)}
                  title="Supprimer"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="admin-homepro-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-homepro-modal-content admin-homepro-large" onClick={(e) => e.stopPropagation()}>
            <div className="admin-homepro-modal-header">
              <h2>{modalMode === 'add' ? 'Ajouter un produit vedette' : 'Modifier le produit vedette'}</h2>
              <button className="admin-homepro-modal-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="admin-homepro-modal-form">
              <div className="admin-homepro-form-grid">
                <div className="admin-homepro-form-group admin-homepro-full-width">
                  <label>Sélectionner un produit (optionnel - pour pré-remplir)</label>
                  <input
                    type="text"
                    placeholder="Rechercher un produit..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="admin-homepro-search-input"
                  />
                  <select
                    value={selectedProductId}
                    onChange={handleProductSelect}
                    style={{ marginTop: '8px' }}
                  >
                    <option value="">Choisir un produit (ou créer manuellement)</option>
                    {filteredProducts.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name} - {product.price.toLocaleString()} DA
                      </option>
                    ))}
                  </select>
                  <p className="admin-homepro-form-hint">
                    Vous pouvez sélectionner un produit existant pour pré-remplir les champs, puis les modifier selon vos besoins.
                  </p>
                </div>

                {/* Multiple Images Upload Section */}
                <div className="admin-homepro-form-group admin-homepro-full-width">
                  <label>Images * (La première image sera l'image principale)</label>

                  {/* Display uploaded images */}
                  {formData.images.length > 0 && (
                    <div className="admin-homepro-uploaded-images-grid">
                      {formData.images.map((img, index) => (
                        <div key={index} className="admin-homepro-uploaded-image-item">
                          <img src={img.url} alt={`Featured ${index + 1}`} />
                          <div className="admin-homepro-image-item-overlay">
                            {index === 0 && (
                              <span className="admin-homepro-primary-badge">Principale</span>
                            )}
                            <div className="admin-homepro-image-item-actions">
                              {index !== 0 && (
                                <button
                                  type="button"
                                  className="admin-homepro-btn-icon admin-homepro-btn-primary-small"
                                  onClick={() => handleSetPrimaryImage(index)}
                                  title="Définir comme principale"
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                  </svg>
                                </button>
                              )}
                              <button
                                type="button"
                                className="admin-homepro-btn-icon admin-homepro-btn-delete-small"
                                onClick={() => handleRemoveImage(index)}
                                title="Supprimer"
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <line x1="18" y1="6" x2="6" y2="18" />
                                  <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload new image */}
                  <div style={{ marginTop: formData.images.length > 0 ? '16px' : '0' }}>
                    <ImageUpload
                      onUploadComplete={handleImageUpload}
                      folder={UPLOAD_FOLDERS.PRODUCTS}
                      maxSize={6}
                      multiple={true}
                      buttonText={formData.images.length > 0 ? 'Ajouter une autre image' : 'Télécharger la première image'}
                    />
                  </div>

                  <p className="admin-homepro-form-hint">
                    {formData.images.length === 0
                      ? 'Téléchargez au moins une image pour le produit vedette'
                      : `${formData.images.length} image(s) téléchargée(s). Vous pouvez en ajouter d'autres.`
                    }
                  </p>
                </div>

                <div className="admin-homepro-form-group admin-homepro-full-width">
                  <label>Titre personnalisé *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    placeholder="Titre affiché sur la page d'accueil"
                  />
                </div>

                <div className="admin-homepro-form-group admin-homepro-full-width">
                  <label>Description *</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows="3"
                    placeholder="Description détaillée pour la page d'accueil"
                  />
                </div>

                <div className="admin-homepro-form-group">
                  <label>Prix affiché *</label>
                  <input
                    type="text"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: À partir de 45,000 DA"
                  />
                </div>

                <div className="admin-homepro-form-group">
                  <label>Badge</label>
                  <select name="badge" value={formData.badge} onChange={handleInputChange}>
                    <option value="">Aucun</option>
                    <option value="Bestseller">Bestseller</option>
                    <option value="Nouveau">Nouveau</option>
                    <option value="Promo">Promo</option>
                    <option value="Hot">Hot</option>
                  </select>
                </div>

                <div className="admin-homepro-form-group">
                  <label>Position de l'image</label>
                  <select name="imagePosition" value={formData.imagePosition} onChange={handleInputChange}>
                    <option value="left">Gauche</option>
                    <option value="right">Droite</option>
                  </select>
                </div>

                <div className="admin-homepro-form-group">
                  <label>Ordre d'affichage</label>
                  <input
                    type="number"
                    name="order"
                    value={formData.order}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>

                <div className="admin-homepro-form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                    />
                    Actif sur la page d'accueil
                  </label>
                </div>

                <div className="admin-homepro-form-group admin-homepro-full-width">
                  <label>Caractéristiques (3 recommandées)</label>
                  <div className="admin-homepro-features-input">
                    <input
                      type="text"
                      value={featureInput}
                      onChange={(e) => setFeatureInput(e.target.value)}
                      placeholder="Ajouter une caractéristique"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                    />
                    <button type="button" onClick={addFeature} className="admin-homepro-btn-add-feature">
                      Ajouter
                    </button>
                  </div>
                  <div className="admin-homepro-features-list">
                    {formData.features.map((feature, index) => (
                      <div key={index} className="admin-homepro-feature-tag">
                        <span>{feature}</span>
                        <button type="button" onClick={() => removeFeature(index)}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="admin-homepro-modal-footer">
                <button type="button" className="admin-homepro-btn admin-homepro-btn-secondary" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="button" className="admin-homepro-btn admin-homepro-btn-primary" onClick={handleSubmit} disabled={loading}>
                  {loading ? 'Enregistrement...' : modalMode === 'add' ? 'Ajouter' : 'Modifier'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHomeProducts;